import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

const DEVIATION_LABELS = {
  log_move: 'Extra step',
  model_move: 'Missing step',
};

const Tooltip = ({ text }) => {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const above = r.top > 140;
    setPos({ left: r.left + r.width / 2, top: above ? r.top - 6 : r.bottom + 6, above });
  };
  return (
    <span ref={ref} className="inline-flex" onMouseEnter={show} onMouseLeave={() => setPos(null)}>
      <span className="w-3.5 h-3.5 rounded-full border border-gray-400 text-gray-400 text-[9px] flex items-center justify-center cursor-help select-none ml-1">i</span>
      {pos && createPortal(
        <span style={{ position: 'fixed', left: pos.left, top: pos.top, transform: pos.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)', zIndex: 9999, pointerEvents: 'none' }}
          className="px-2 py-1.5 text-xs bg-gray-800 text-white rounded shadow-lg w-52 text-left whitespace-normal">
          {text}
        </span>,
        document.body
      )}
    </span>
  );
};

const KpiCard = ({ label, value, sub, tooltip }) => (
  <div className="flex flex-col gap-0.5 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
    <span className="text-xs text-gray-500 flex items-center">
      {label}
      {tooltip && <Tooltip text={tooltip} />}
    </span>
    <span className="text-lg font-semibold text-gray-800">{value}</span>
    {sub && <span className="text-xs text-gray-400">{sub}</span>}
  </div>
);

const ConformanceSummary = ({ results }) => {
  const {
    compliant_trace_percentage,
    most_common_deviations,
    variant_results,
  } = results;

  const nonConf = variant_results.filter(v => v.fitness < 0.95);
  const totalNCTraces = nonConf.reduce((s, v) => s + v.trace_count, 0);
  const avgSeverity = totalNCTraces > 0
    ? ((1 - nonConf.reduce((s, v) => s + v.fitness * v.trace_count, 0) / totalNCTraces) * 100).toFixed(1)
    : null;

  return (
    <div className="flex flex-col gap-4">
      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-2">
        <KpiCard
          label="Conformance Rate"
          value={`${compliant_trace_percentage}%`}
          sub="of all traces"
          tooltip="The share of all traces that are fully conformant. E.g. 80% means 4 out of 5 traces had no deviations."
        />
        <KpiCard
          label="Avg Deviation Severity"
          value={avgSeverity != null ? `${avgSeverity}%` : '—'}
          sub={avgSeverity != null ? "of non-conformant traces" : "all traces conformant"}
          tooltip="Average degree of deviation for non-conformant traces only, expressed as a weighted mean of edit-distance severity across failing variants. 0% means minimal deviation; 100% means complete mismatch."
        />
      </div>

      {/* Most common deviations */}
      {most_common_deviations.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-1 flex items-center">
            Most Common Deviations
            <Tooltip text="A deviation is any place where an observed trace differs from the expected process flow, such as a required step that was skipped or an unexpected step that occurred." />
          </h4>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-1/2">Activity</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-right px-3 py-2 font-medium">Count</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {most_common_deviations.slice(0, 5).map((dev, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-700 max-w-[200px] truncate" title={dev.activity}>
                      {dev.activity}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                        dev.move_type === 'log_move' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {DEVIATION_LABELS[dev.move_type] || dev.move_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{dev.occurrence_count}×</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConformanceSummary;
